import { useEffect, useState } from 'react';
import Link from 'next/link';

import { Box, Heading, Icon, Paragraph, Tag } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconChevronRightCircleFilled,
  IconExclamationCircleFilled,
  IconHideFilled,
  IconHomesFilled,
  IconSparkleFilled,
} from '@zillow/constellation-icons';

import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyRentGuaranteeWebApiHandler_getRentalProtectionStateForListingMultiPolicy,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { ROUTES } from '../../../constants/urls';

import ZillowInsuranceServicesLogo from '../../../components/RentalProtection/shared/ZillowInsuranceServicesLogo';
import RentGuaranteeActiveModal from '../../modals/RentalProtection/RentGuaranteeActiveModal';

import {
  emitLearnMoreBannerClickEvent,
  emitManageRentalProtectionBannerClickEvent,
  EVENT_IDS,
} from '../../../analytics/events/rentalProtectionBanner';
import { RENT_GUARANTEE_LISTING_STATE } from '../../../types/rentalProtection';
import { useAuth } from '../../AuthProvider';

import type { TagTone } from '../../../utils/statusTagUtils';

type Props = {
  listingAlias: string;
};

export const getTextColor = (rentGuaranteeListingState: string, hasPremiumPaymentError = false) => {
  if (hasPremiumPaymentError) {
    return 'text.feedback.critical.hero';
  }
  switch (rentGuaranteeListingState) {
    case RENT_GUARANTEE_LISTING_STATE.PENDING_CANCELLATION:
    case RENT_GUARANTEE_LISTING_STATE.SUNSET:
      return 'text.accent.orange.hero';
    case RENT_GUARANTEE_LISTING_STATE.CANCELLED:
      return 'text.feedback.critical.hero';
    default:
      return 'text.neutral';
  }
};

export const getBannerIcon = (rentGuaranteeListingState: string, hasPremiumPaymentError = false) => {
  if (hasPremiumPaymentError) {
    return (
      <Icon
        size="lg"
        css={{ color: 'icon.feedback.critical.hero', marginRight: '200' }}
        render={<IconExclamationCircleFilled />}
      />
    );
  }
  switch (rentGuaranteeListingState) {
    case RENT_GUARANTEE_LISTING_STATE.PENDING_CANCELLATION:
    case RENT_GUARANTEE_LISTING_STATE.SUNSET:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.feedback.notify.hero', marginRight: '200' }}
          render={<IconExclamationCircleFilled />}
        />
      );
    case RENT_GUARANTEE_LISTING_STATE.CANCELLED:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.feedback.critical.hero', marginRight: '200' }}
          render={<IconHideFilled />}
        />
      );
    case RENT_GUARANTEE_LISTING_STATE.ACTIVE:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.feedback.success.hero', marginRight: '200' }}
          render={<IconCheckmarkCircleFilled />}
        />
      );
    case RENT_GUARANTEE_LISTING_STATE.EXPIRED:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.feedback.critical.hero', marginRight: '200' }}
          render={<IconHideFilled />}
        />
      );
    case RENT_GUARANTEE_LISTING_STATE.ACTIVE_RENEW:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.onImpact.feedback.warning.hero', marginRight: '200' }}
          render={<IconSparkleFilled />}
        />
      );
    case RENT_GUARANTEE_LISTING_STATE.MISSING_PAYMENT_METHOD:
      return (
        <Icon
          size="lg"
          css={{ color: 'icon.onImpact.feedback.warning.hero', marginRight: '200' }}
          render={<IconExclamationCircleFilled />}
        />
      );
    default:
      return (
        <Icon size="lg" css={{ color: 'icon.accent.brand.hero', marginRight: '200' }} render={<IconHomesFilled />} />
      );
  }
};

const RentGuaranteeBannerMultiPolicy = ({ listingAlias }: Props) => {
  const { user, isAuthorized } = useAuth();
  const [showActiveModal, setShowActiveModal] = useState(false);

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    {
      enabled: Boolean(listingAlias) && isAuthorized,
      meta: {
        tags: ['propertyDetailsOverview', `propertyDetailsOverview-${listingAlias}`],
        context: { api: 'overview', details: 'RentGuaranteeBannerMultiPolicy' },
      },
    },
  );

  const isEligible = overviewData?.rentGuarantee?.isEligible ?? false;
  const isEnabled = overviewData?.rentGuarantee?.isEnabled ?? false;

  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    { listingAlias, includeLeases: false },
    {
      enabled: Boolean(listingAlias) && isAuthorized && isEligible,
      meta: {
        tags: ['propertyWebAnalytics', `propertyWebAnalytics-${listingAlias}`],
        context: { api: 'webAnalytics', details: 'RentGuaranteeBannerMultiPolicy' },
      },
    },
  );

  const { data } = useRentalManagerUserPropertyRentGuaranteeWebApiHandler_getRentalProtectionStateForListingMultiPolicy(
    { listingAlias },
    {
      enabled: Boolean(listingAlias) && isAuthorized && isEligible,
      meta: {
        tags: ['rentalProtectionStateMultiPolicy', `rentalProtectionStateMultiPolicy-${listingAlias}`],
        context: {
          api: 'getRentalProtectionStateForListingMultiPolicy',
          details: 'RentGuaranteeBannerMultiPolicy',
        },
      },
    },
  );

  const getNavigationRoute = () => {
    const pathname = ROUTES.RENTAL_PROTECTION_MANAGE_POLICIES;
    if (data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.DROPPED || isEnabled) {
      return { pathname, query: { propertyId: listingAlias } };
    }
    return { pathname, query: { propertyId: listingAlias, productIntroType: 'sohIllustrative' } };
  };

  useEffect(() => {
    if (data?.displayEffectivePolicyScreen) {
      setShowActiveModal(true);
    }
  }, [data?.displayEffectivePolicyScreen]);

  const isInLightweightOptInState = data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.LIGHTWEIGHT_OPT_IN;

  const handleBannerClick = () => {
    if (!webAnalyticsData) return;
    const csParams = { currentProperty: webAnalyticsData, user: user ?? null };
    if (data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.NOT_ENROLLED || isInLightweightOptInState) {
      emitLearnMoreBannerClickEvent({
        ...csParams,
        entryPoint: 'DashboardOverviewPage',
      });
    } else {
      emitManageRentalProtectionBannerClickEvent(csParams);
    }
  };

  if (!data?.rentGuaranteeStateTitle) {
    return null;
  }

  return (
    <>
      {showActiveModal && (
        <RentGuaranteeActiveModal
          listingAlias={listingAlias}
          legalDisclaimer={data.disclaimer}
          coverageSummaryUrl={data.coverageSummaryUrl}
          coverageAmount={data.coverageAmount}
          onClose={() => setShowActiveModal(false)}
          onSubmit={() => setShowActiveModal(false)}
        />
      )}
      <Link
        href={getNavigationRoute()}
        data-testid="RentGuaranteeBannerMultiPolicy"
        onClick={handleBannerClick}
        data-eventid={
          data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.NOT_ENROLLED || isInLightweightOptInState
            ? EVENT_IDS.LEARN_MORE_BANNER_CLICK
            : EVENT_IDS.MANAGE_BANNER_CLICK
        }
        style={{ textDecoration: 'none', display: 'block' }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '400',
            backgroundColor: isInLightweightOptInState ? 'bg.accent.blue.soft' : 'bg.neutral',
            cursor: 'pointer',
          }}
        >
          <Box
            css={{
              width: '100%',
              marginBlock: '0',
              padding: '0',
            }}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Box css={{ display: 'flex', flexShrink: 0 }}>
                  {getBannerIcon(data?.rentGuaranteeListingState, data?.hasPremiumPaymentError)}
                </Box>
                <Box css={{ display: 'flex', flexDirection: 'column' }}>
                  {data?.tagStatus && data?.tagText ? (
                    <Box css={{ mdDown: { display: 'none' } }}>
                      <ZillowInsuranceServicesLogo width="150" />
                    </Box>
                  ) : (
                    <ZillowInsuranceServicesLogo width="150" />
                  )}
                  <Box css={{ display: 'flex', flexDirection: { base: 'column-reverse', md: 'row' } }}>
                    <Heading level={5} fontFamily="heading-alt" css={{ marginRight: '400' }} data-test="title">
                      {data?.rentGuaranteeStateTitle}
                    </Heading>
                    {data?.tagStatus && data?.tagText ? (
                      <Box css={{ marginBottom: { base: '200', md: '0' } }}>
                        <Tag tone={data?.tagStatus as TagTone} icon={<Icon render={<IconSparkleFilled />} />}>
                          {data?.tagText}
                        </Tag>
                      </Box>
                    ) : null}
                  </Box>
                  <Paragraph
                    data-test="description"
                    css={{
                      textStyle: { base: 'body', mdDown: 'body-sm' },
                      color: getTextColor(data?.rentGuaranteeListingState, data?.hasPremiumPaymentError),
                    }}
                  >
                    {data?.rentGuaranteeStateDescription}
                  </Paragraph>
                </Box>
              </Box>
              <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexShrink: 0 }}>
                <Box css={{ mdDown: { display: 'none' } }}>
                  <Paragraph textStyle="body-bold" css={{ marginRight: '200', color: 'text.express.insight.strong' }}>
                    {data?.ctaText}
                  </Paragraph>
                </Box>
                <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconChevronRightCircleFilled />} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </>
  );
};

export default RentGuaranteeBannerMultiPolicy;
